import { Box, StyleProps, VStack } from "@chakra-ui/react";
import { Fragment, TouchEvent, useRef, useState } from "react";

import { $map } from "foundation-ts/array";
import { $count, $defined, $isuuid, $ok } from "foundation-ts/commons";
import { Nullable, uint32, UUID } from "foundation-ts/types";
import { SignatureDto } from "g1-commons/lib/doxecureClientTypes";
import { SignatureType } from "g1-commons/lib/doxecureModelTypes";

import { SignerCard } from "@components/Cards/SignerCard";
import { SectionBoxFooter } from "@components/SectionBox/SectionBoxFooter";
import { DropZone } from "@components/SignersManager/DropZone";
import { SignatureGroup } from "@/components/SignersManager/SignatureGroup";
import { localtrans } from "@utils/LocalTranslations";
import { CustomItemType } from "@/utils/TypesAndConstants";

interface SignaturesManagerProps {
    signatures: Nullable<SignatureDto[]>;
    limit: number;
    soloMode?: boolean;
    sectionStyle: StyleProps;
    itemStyle: CustomItemType;
    onSignersChange: (signatures: SignatureDto[]) => void;
    onRemoveSigner: (apid: UUID) => void;
    onAddSigner: () => void;
}

export const SignaturesManager = (props: SignaturesManagerProps) => {
    const { signatures, limit, soloMode = false, sectionStyle, itemStyle, onSignersChange, onRemoveSigner, onAddSigner } = props;
    const [dragOverRank, setDragOverRank] = useState<Nullable<number>>(null);
    const [touchOverFirstZone, setTouchOverFirstZone] = useState(false);
    const [touchOverLastZone, setTouchOverLastZone] = useState(false);
    const [touchOverBetweenZones, setTouchOverBetweenZones] = useState<boolean[]>(Array(limit).fill(false));
    const draggedSignatureRef = useRef<Nullable<SignatureDto>>(null);
    const cloneRef = useRef<Nullable<HTMLDivElement>>(null);
    const isDraggingRef = useRef(false);

    const groups = groupSignersByRank(signatures);
    const sortedRanks = getSortedRanks(signatures);

    const handleDragStart = (signature: SignatureDto) => {
        if (soloMode) { return; }

        draggedSignatureRef.current = signature;
        isDraggingRef.current = true;
    }

    const handleDragEnd = () => {
        draggedSignatureRef.current = null;
        isDraggingRef.current = false;
        if ($ok(cloneRef.current)) {
            document.body.removeChild(cloneRef.current!);
            cloneRef.current = null;
        }
        setDragOverRank(null);
        setTouchOverFirstZone(false);
        setTouchOverLastZone(false);
        setTouchOverBetweenZones(Array(limit).fill(false));
    }

    const handleDropOnGroup = (rank: number) => {
        const draggedSignature = draggedSignatureRef.current;
        if (!$ok(signatures) || !$ok(draggedSignature) || draggedSignature?.rank === rank) { 
            handleDragEnd();
            return;
        }

        const updated = $map(signatures, sign => sign.signer.apid === draggedSignature!.signer.apid ? { ...sign, rank } as SignatureDto : sign);
        handleDragEnd();
        onSignersChange(recalculateRanks(updated));
    }

    const handleDropToFirst = () => {
        const draggedSignature = draggedSignatureRef.current;
        if (!$ok(signatures) || !$ok(draggedSignature)) { 
            handleDragEnd();
            return;
        }

        const newFirstSignature = {...draggedSignature, rank: 0 } as SignatureDto;
        const othersSignatures = $map(signatures?.filter(sign => sign.signer.apid !== draggedSignature?.signer.apid), sign => { return {...sign, rank: sign.rank+1} as SignatureDto });
        handleDragEnd();
        onSignersChange(recalculateRanks([newFirstSignature, ...othersSignatures]));
    }

    const handleDropBetween = (beforeRank: number, afterRank: number) => {
        const draggedSignature = draggedSignatureRef.current;
        if (!$ok(signatures) || !$ok(draggedSignature)) { 
            handleDragEnd();
            return;
        }

        const newRank = (beforeRank! + afterRank!) / 2;
        const updated = $map(signatures, sign => sign.signer.apid === draggedSignature!.signer.apid ? { ...sign, rank: newRank } as SignatureDto : sign);
        handleDragEnd();
        onSignersChange(recalculateRanks(updated));
    }

    const handleDropToLast = () => {
        const draggedSignature = draggedSignatureRef.current;
        if (!$ok(signatures) || !$ok(draggedSignature)) { 
            handleDragEnd();
            return;
        }

        const maxRank = Math.max(...signatures!.map(sign => sign.rank));
        const newLastSignature = {...draggedSignature, rank: maxRank + 1 } as SignatureDto;
        const updated = signatures!.filter(sign => sign.signer.apid !== draggedSignature?.signer.apid);
        handleDragEnd();
        onSignersChange(recalculateRanks([...updated, newLastSignature]));
    }

    const handleTouchStart = (e: TouchEvent, signature: SignatureDto) => {
        if (soloMode) { return; }

        const usedStyles = getComputedStyle(document.getElementById(`${SIGNER_GROUP_ID}-0`)!);

        const originalElement = e.currentTarget as HTMLDivElement;
        const rect = originalElement.getBoundingClientRect();
        const ghost = originalElement.cloneNode(true) as HTMLDivElement;
        ghost.style.background = usedStyles.background;
        ghost.style.borderRadius = usedStyles.borderRadius;
        ghost.style.position = 'absolute';
        ghost.style.top = `${rect.top + window.scrollY}px`;
        ghost.style.left = `${rect.left + window.scrollX}px`;
        ghost.style.width = `${rect.width}px`;
        ghost.style.height = `${rect.height}px`;
        ghost.style.pointerEvents = 'none';
        ghost.style.zIndex = '9999';
        ghost.style.transform = 'translate(0, 0)';

        document.body.appendChild(ghost);
        cloneRef.current = ghost;        
        draggedSignatureRef.current = signature;
        isDraggingRef.current = true;
    }

    const handleTouchMove = (e: TouchEvent) => {
        const draggedSignature = draggedSignatureRef.current;
        if (!$ok(signatures) || !$ok(draggedSignature)) { 
            handleDragEnd();
            return;
        }
    
        const touch = e.touches[0];
        if ($ok(cloneRef.current)) {
            cloneRef.current!.style.top = `${touch.clientY + window.scrollY - cloneRef.current!.offsetHeight / 2}px`;
            cloneRef.current!.style.left = `${touch.clientX + window.scrollX - cloneRef.current!.offsetWidth / 2}px`;
        }
    
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
        setTouchOverFirstZone(elements.find(el => el.id === FIRST_DROP_ZONE_ID) !== undefined);
        setTouchOverLastZone(elements.find(el => el.id === LAST_DROP_ZONE_ID) !== undefined);
        const betweenZoneIdElt = elements.find(el => el.id.startsWith(BETWEEN_DROP_ZONE_ID));
        if ($ok(betweenZoneIdElt)) {
            const index = parseInt(betweenZoneIdElt!.id.split('-')[1], 10);
            setTouchOverBetweenZones(touchOverBetweenZones.map((_, i) => i === index));
        } else if (touchOverBetweenZones.findIndex(value => value === true) !== -1) {
            setTouchOverBetweenZones((Array(limit).fill(false)));
        }
        const groupElement = elements.find(el => el.id.startsWith(SIGNER_GROUP_ID));
        if ($defined(groupElement)) {
            const rankAttr = groupElement!.getAttribute('data-rank');
            if ($defined(rankAttr) !== null) {
                const rank = Number(rankAttr);
                if (rank !== draggedSignature!.rank) {
                    setDragOverRank(rank);
                }
            }
        } else {
            setDragOverRank(null);
        }
    }

    const handleTouchEnd = () => {
        const draggedSignature = draggedSignatureRef.current;
        if (!$ok(signatures) || !$ok(draggedSignature)) { 
            handleDragEnd();
            return;
        }
        if (touchOverFirstZone) {
            handleDropToFirst()
            ;
            return;
        }
        if (touchOverLastZone) {
            handleDropToLast();
            return;
        }
        const indexBetweenZone = touchOverBetweenZones.findIndex(value => value === true);
        if (indexBetweenZone !== -1) {
            handleDropBetween(indexBetweenZone, sortedRanks[indexBetweenZone+1]);
            return;
        }
        if ($ok(dragOverRank) && dragOverRank !== draggedSignature!.rank) {
            handleDropOnGroup(dragOverRank!);
        } else {
            handleDragEnd();
        }
    }

    const handleRemoveSigner = (apid?: Nullable<UUID>) => {
        if (!$ok(signatures) ||!$isuuid(apid)) { return; }

        const signerToWork = signatures!.find(sign => sign.signer.apid === apid);
        if (!$ok(signerToWork)) { return; }

        const signersInSameRank = signatures!.filter(sign => sign.rank === signerToWork!.rank);
        if (soloMode || $count(signersInSameRank) === 1) {
            onRemoveSigner(apid!);
        } else {
            const noChangedSigners = signatures!.filter(sign => sign.signer.apid !== apid && sign.rank <= signerToWork!.rank);
            const updated = $map(signatures?.filter(sign => sign.rank > signerToWork!.rank), sign => { return {...sign, rank: sign.rank+1} as SignatureDto });
            onSignersChange(recalculateRanks([...noChangedSigners, {...signerToWork, rank: signerToWork!.rank+1 } as SignatureDto, ...updated]));
        }
    }

    const handleQualifiedSwitchChange = (signature: SignatureDto, checked: boolean) => {
        if (!$ok(signatures)) { return; }

        signature.signatureType = checked ? SignatureType.QSCD : SignatureType.UserChoice;
        onSignersChange(signatures!);
    }

    return (
        <>
            <Box
                color={sectionStyle.color}
                overflow={sectionStyle.overflow}
                width={sectionStyle.width}
                fontWeight={sectionStyle.fontWeight}
            >
                    <VStack align="stretch">
                        <DropZone id={`${FIRST_DROP_ZONE_ID}`}
                            text={localtrans("SignaturesManager.DropFirst")}
                            isVisible={isDraggingRef.current && shouldShowDropZone(signatures, draggedSignatureRef.current, null, sortedRanks[0])}
                            isTouchOver={touchOverFirstZone}
                            onDrop={handleDropToFirst}
                            onTouchEnd={handleTouchEnd}
                        />

                        {sortedRanks.map((rank, index) => (
                            <Fragment key={rank} >
                                <SignatureGroup id={`${SIGNER_GROUP_ID}-${rank}`}
                                    title={`Rang ${rank} (${$count(groups[rank])} signataires ${soloMode ? 'individuels' : 'en parallèle'})`}
                                    rank={rank}
                                    multipleSigners={$count(groups[rank]) > 1}
                                    sectionStyle={sectionStyle}
                                    isDragOver={dragOverRank === rank}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        if ($ok(draggedSignatureRef.current) && draggedSignatureRef.current!.rank !== rank) {
                                            setDragOverRank(rank);
                                        }
                                    }}
                                    onDragLeave={() => setDragOverRank(null)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        handleDropOnGroup(rank);
                                        setDragOverRank(null);
                                    }}
                                >
                                    {groups[rank].map(signature => (
                                        <SignerCard
                                            key={signature.signer.apid}
                                            item={signature.signer}
                                            itemStyle={itemStyle}
                                            opacity={draggedSignatureRef.current?.signer.apid === signature.signer.apid ? 0.5 : 1}
                                            qualifiedSwitch={signature!.signatureType === SignatureType.QSCD}
                                            draggable={!soloMode}
                                            onQualifiedSwitchChange={(checked) => handleQualifiedSwitchChange(signature, checked)}
                                            onRemoveItemClick={() => handleRemoveSigner(signature.signer.apid)}
                                            onDragStart={() => handleDragStart(signature)}
                                            onDragEnd={handleDragEnd}
                                            onTouchStart={(e) => handleTouchStart(e, signature)}
                                            onTouchMove={handleTouchMove}
                                            onTouchEnd={handleTouchEnd}
                                        />
                                    ))}
                                </SignatureGroup>

                                {(index < sortedRanks.length - 1) && (
                                    <DropZone id={`${BETWEEN_DROP_ZONE_ID}-${index}`}
                                        text={localtrans("SignaturesManager.DropBetween")}
                                        isVisible={isDraggingRef.current && shouldShowDropZone(signatures, draggedSignatureRef.current, rank, sortedRanks[index + 1])}
                                        isTouchOver={touchOverBetweenZones[index]}
                                        onDrop={() => handleDropBetween(rank, sortedRanks[index + 1])}
                                        onTouchEnd={() => handleDropBetween(rank, sortedRanks[index + 1])}
                                    />
                                )}
                            </Fragment>
                        ))}

                        <DropZone id={`${LAST_DROP_ZONE_ID}`}
                            text={localtrans("SignaturesManager.DropLast")}
                            isVisible={isDraggingRef.current && shouldShowDropZone(signatures, draggedSignatureRef.current, sortedRanks[sortedRanks.length - 1], null)}
                            isTouchOver={touchOverLastZone}
                            onDrop={handleDropToLast}
                            onTouchEnd={handleTouchEnd}
                        />

                        <SectionBoxFooter component="contacts" title={localtrans('AddSignerButton')} disabled={$count(signatures) >= limit} borderRadius="xl" onClick={onAddSigner} /> 
                    </VStack>
            </Box>
        </>
    );
}

const SIGNER_GROUP_ID = "signature-group";
const FIRST_DROP_ZONE_ID = "firstzone";
const LAST_DROP_ZONE_ID = "lastzone";
const BETWEEN_DROP_ZONE_ID = "betweenzone";

interface SignerGroups {
  [rank: number]: SignatureDto[];
}

const groupSignersByRank = (signatures: Nullable<SignatureDto[]>): SignerGroups => {
    if ($count(signatures) === 0) { return {}; }

    const groups: SignerGroups = {};
    signatures!.forEach(sign => {
        if (!groups[sign.rank]) {
            groups[sign.rank] = [];
        }
        groups[sign.rank].push(sign);
    });

    Object.keys(groups).forEach((rank) => {
        groups[Number(rank)].sort((a, b) => a.signer.lastName.localeCompare(b.signer.lastName));
    });

    return groups;
}

const getSortedRanks = (signatures: Nullable<SignatureDto[]>): number[] => {
    if ($count(signatures) === 0) { return []; }

    const groups = groupSignersByRank(signatures);
    return Object.keys(groups).map(Number).sort((a, b) => a - b);
}

const recalculateRanks = (updatedSigners: SignatureDto[]) => {
    const groups: SignerGroups = {};
    updatedSigners.forEach((signer) => {
        if (!groups[signer.rank]) {
            groups[signer.rank] = [];
        }
        groups[signer.rank].push(signer);
    });

    const sortedRanks = Object.keys(groups).map(Number).sort((a, b) => a - b);
    const recalculated: SignatureDto[] = [];

    sortedRanks.forEach((oldRank, newRank) => {
        groups[oldRank].forEach((signer) => {
            recalculated.push({ ...signer, rank: newRank as uint32 });
        });
    });

    return recalculated;
}

const shouldShowDropZone = (signatures: Nullable<SignatureDto[]>, draggedSigner: Nullable<SignatureDto>, beforeRank: Nullable<number>, afterRank: Nullable<number>): boolean => {
    if (($count(signatures) === 0) || !$defined(draggedSigner)) { return false; }

    // If signer is in a group, then all zones must be shown
    const signersInSameRank = signatures!.filter(sign => sign.rank === draggedSigner!.rank);
    if ($count(signersInSameRank) > 1) { return true; }

    // Here, signer is alone, so we must hide adjacent drop zones
    if ((beforeRank === null && afterRank === draggedSigner!.rank) || // Before zone
        (afterRank === null && beforeRank === draggedSigner!.rank) || // After zone
        (beforeRank === draggedSigner!.rank || afterRank === draggedSigner!.rank)) // Between zone
    { 
        return false; 
    }

    return true;
};